// src/pages/Manager/FloorThisMorning.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  New. The top of the manager's day. Tyler: avatars and not a
//               leaderboard, where the floor is in the stages, and pump these
//               people up.
//
// THE FLOOR, THIS MORNING. THREE THINGS A MANAGER WANTS BEFORE THE MEETING.
//
//   1. Who warmed up. The salesperson routine on Today writes one row when it
//      finishes, and this is where that row becomes useful: a face with a
//      tick, and how many that person has to call back.
//   2. Who to recognise. Anybody nobody has noticed in seven days gets named,
//      quietly, to the manager only. Gallup's figure is that one worker in
//      three was recognised in the last week and the unrecognised are twice as
//      likely to leave. This line is how the first number gets better.
//   3. What happened on the floor. Marks given this week as moments, with
//      yesterday's labelled for calling out at the meeting, because a good
//      meeting opens by recognising yesterday and nobody remembers what they
//      gave at four in the afternoon.
//
// Then the track (StageTrack.jsx), which is where everybody is in the steps.
//
// NOTHING HERE IS A TOTAL. No count of marks beside a name, no sort by
// anything but time. tests/marks.test.mjs reads this file and fails if it
// grows either, because both are a leaderboard however they are styled.
//
// Reads that fail are NOT KNOWN and say so. A failed recognitions read shown
// as "nobody recognised this week" would send a manager to recognise people
// they recognised yesterday.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Flex, HStack, VStack, Text, Button, Icon, useDisclosure } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { Check, ChevronRight, Undo2 } from 'lucide-react'
import PersonAvatar from '../Team/PersonAvatar.jsx'
import Medallion from '../../components/ui/Medallion.jsx'
import GiveMark from './GiveMark.jsx'
import StageTrack from './StageTrack.jsx'
import { markByKey } from '../../content/marks'
import { firstNameFor } from '../../lib/names'
import { unsoldLabel } from '../../lib/morningRoutine.js'
import { dayKey, addDays, parseDay } from '../../lib/localDate'
import {
  listSince, listMorningEvents, listFloorProgress, listPeople, undoMark,
  morningFor, dueForRecognition, floorLine, groupByDay, lastMarkFor, dayLabel,
} from '../../lib/recognitions'

const WEEK_BACK = 8

function Tick() {
  return (
    <Flex boxSize="18px" borderRadius="full" bg="success" color="onSuccess" align="center" justify="center" border="2px solid" borderColor="card">
      <Icon as={Check} boxSize="10px" strokeWidth={3.5} />
    </Flex>
  )
}

// THE PANEL ITSELF, drawn from data it is handed. Split from the loader so
// the dev preview (src/pages/dev/MarksPreview.jsx) can draw it with example
// data, which is the only way to look at it before a real floor uses it.
export function FloorMorningView({ floor, marks, events, people = {}, userId, today = dayKey(), onOpen, onUndo }) {
  const byId = { ...people }
  for (const p of floor) byId[p.id] = p

  const loading = marks === undefined || events === undefined
  const morning = events ? morningFor(floor, events, today) : null
  const morningOf = Object.fromEntries((morning || []).map((m) => [m.id, m]))
  const due = marks ? dueForRecognition(floor, marks) : []
  const groups = marks ? groupByDay(marks, today) : []
  const nameFor = (id) => (id === userId ? 'you' : firstNameFor(byId[id]) || 'somebody')
  const open = (person) => onOpen?.(person)
  const undo = (id) => onUndo?.(id)

  return (
    <Box bg="card" border="1px solid" borderColor="line" borderRadius="cardLg" p={{ base: 5, md: 6 }}>
      <VStack align="stretch" spacing={6}>
        <Box>
          <Text textStyle="eyebrow" color="inkDim" mb={1}>
            THE FLOOR THIS MORNING
          </Text>
          <Text fontSize="lg" fontWeight={600} letterSpacing="-0.01em">
            {loading ? 'Reading the floor' : morning ? floorLine(morning) : 'Could not read who warmed up.'}
          </Text>
          <Text fontSize="body-sm" color="inkMuted" mt={0.5}>
            Tap anybody to recognise something you saw them do.
          </Text>
        </Box>

        {/* The faces. The tick is the routine on their Today, done before
            the shift. No mark counts here, ever. */}
        <HStack
          spacing={1}
          overflowX="auto"
          mx={-2}
          pb={1}
          sx={{
            scrollSnapType: 'x proximity',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            '& > *': { scrollSnapAlign: 'start' },
          }}
        >
          {floor.map((p) => {
            const m = morningOf[p.id]
            const sub = m?.warmedUp ? (m.unsold > 0 ? `${unsoldLabel(m.unsold)} to call` : 'warmed up') : undefined
            return (
              <PersonAvatar
                key={p.id}
                person={p}
                onClick={() => open(p)}
                label={`Recognise ${firstNameFor(p) || 'this person'}`}
                badge={m?.warmedUp ? <Tick /> : null}
                sub={sub}
              />
            )
          })}
        </HStack>

        {/* WHO NOBODY HAS NOTICED. Manager only, about the manager's week
            rather than the person's. Hidden when the read failed, because a
            wrong version of this line sends somebody to recognise the
            person they recognised yesterday. */}
        {marks && due.length > 0 && (
          <HStack
            align="flex-start"
            spacing={3}
            bg="goldSoft"
            borderRadius="md"
            px={4}
            py={3}
          >
            <Medallion mark="kept" size={28} muted mt="1px" />
            <Box>
              <Text fontSize="body-sm" lineHeight={1.5}>
                {due.length === floor.length ? (
                  <>Nobody on the floor has been recognised this week.</>
                ) : (
                  <>
                    Nobody has recognised{' '}
                    {due.map((p, i) => (
                      <Box as="span" key={p.id}>
                        <Box
                          as="button"
                          type="button"
                          fontWeight={600}
                          textDecoration="underline"
                          textDecorationColor="lineStrong"
                          textUnderlineOffset="3px"
                          onClick={() => open(p)}
                        >
                          {firstNameFor(p) || 'Somebody'}
                        </Box>
                        {i < due.length - 2 ? ', ' : i === due.length - 2 ? ' or ' : ''}
                      </Box>
                    ))}{' '}
                    this week.
                  </>
                )}
              </Text>
              <Text fontSize="xs" color="inkMuted" mt={0.5} lineHeight={1.5}>
                People noticed every week stay. It takes ten seconds and one thing you saw.
              </Text>
            </Box>
          </HStack>
        )}

        {/* THE WEEK, AS MOMENTS. Yesterday's are what gets called out at
            the meeting, so they say so. */}
        {marks && groups.length > 0 && (
          <Box>
            <HStack justify="space-between" align="baseline" mb={2}>
              <Text fontSize="body" fontWeight={600}>
                Noticed on the floor
              </Text>
              <Button as={RouterLink} to="/manager/morning-meeting/" size="xs" variant="ghost" rightIcon={<ChevronRight size={13} />}>
                Morning meeting
              </Button>
            </HStack>
            <VStack align="stretch" spacing={4}>
              {groups.slice(0, 4).map((g) => (
                <Box key={g.label}>
                  <Text fontSize="xs" fontFamily="mono" color={g.label === 'yesterday' ? 'accent.500' : 'inkDim'} mb={1.5} textTransform="uppercase" letterSpacing="0.06em">
                    {g.label === 'yesterday' ? 'yesterday, call these out' : g.label}
                  </Text>
                  <VStack align="stretch" spacing={0}>
                    {g.items.map((r) => {
                      const m = markByKey(r.mark)
                      const canUndo = r.given_by === userId && Date.now() - new Date(r.created_at).getTime() < 23 * 3600 * 1000
                      return (
                        <HStack key={r.id} spacing={3} py={2} borderTop="1px solid" borderColor="line" align="flex-start">
                          <Medallion mark={r.mark} size={32} />
                          <Box flex={1} minW={0}>
                            <Text fontSize="body-sm" lineHeight={1.4}>
                              <Text as="span" fontWeight={600}>{firstNameFor(byId[r.given_to]) || 'Somebody'}</Text>
                              <Text as="span" color="inkMuted">{' · '}{m?.name || 'a mark'}</Text>
                            </Text>
                            {r.note && (
                              <Text fontSize="sm" color="inkMuted" lineHeight={1.45} mt={0.5}>
                                “{r.note}”
                              </Text>
                            )}
                            <Text fontSize="xs" color="inkDim" mt={0.5}>
                              from {nameFor(r.given_by)}
                            </Text>
                          </Box>
                          {canUndo && (
                            <Button size="xs" variant="ghost" leftIcon={<Undo2 size={12} />} onClick={() => undo(r.id)} color="inkDim">
                              Undo
                            </Button>
                          )}
                        </HStack>
                      )
                    })}
                  </VStack>
                </Box>
              ))}
            </VStack>
          </Box>
        )}

        {marks === null && (
          <Text fontSize="sm" color="inkDim">
            Could not read this week's marks right now.
          </Text>
        )}
      </VStack>
    </Box>
  )
}

export default function FloorThisMorning({ floor, dealershipId, userId }) {
  const today = dayKey()
  const [marks, setMarks] = useState(undefined) // undefined loading, null failed
  const [events, setEvents] = useState(undefined)
  const [progress, setProgress] = useState(undefined)
  const [people, setPeople] = useState({})
  const [picked, setPicked] = useState(null)
  const sheet = useDisclosure()

  const ids = useMemo(() => floor.map((p) => p.id), [floor])

  const load = useCallback(async () => {
    const since = parseDay(addDays(today, -WEEK_BACK)).toISOString()
    const startOfToday = parseDay(today).toISOString()
    const [r, e, p] = await Promise.all([
      listSince(dealershipId, since),
      listMorningEvents(ids, startOfToday),
      listFloorProgress(ids),
    ])
    setMarks(r.data)
    setEvents(e.data)
    setProgress(p.data)
    if (r.data && r.data.length) {
      const who = await listPeople(r.data.flatMap((x) => [x.given_by, x.given_to]))
      if (who.data) setPeople(Object.fromEntries(who.data.map((u) => [u.id, u])))
    }
  }, [dealershipId, ids, today])

  useEffect(() => {
    if (!dealershipId) return
    load()
  }, [dealershipId, load])

  if (!dealershipId || floor.length === 0) return null

  const open = (person) => {
    setPicked(person)
    sheet.onOpen()
  }

  const undo = async (id) => {
    const { error } = await undoMark(id)
    if (!error) load()
  }

  const lastFor = picked && marks ? lastMarkFor(picked.id, marks) : null

  return (
    <>
      <FloorMorningView
        floor={floor}
        marks={marks}
        events={events}
        people={people}
        userId={userId}
        today={today}
        onOpen={open}
        onUndo={undo}
      />

      {progress !== undefined && <StageTrack people={floor} progress={progress} />}

      <GiveMark
        person={picked}
        isOpen={sheet.isOpen}
        onClose={sheet.onClose}
        dealershipId={dealershipId}
        givenBy={userId}
        lastMark={lastFor ? { mark: lastFor.mark, when: dayLabel(lastFor.created_at, today) } : null}
        onGiven={() => load()}
      />
    </>
  )
}
